<div class="report-bug-drawer" [class.report-bug-drawer--open]="isOpen" *ngIf="isOpen">
    <div class="report-bug-drawer__header">
        <h2 class="report-bug-drawer__title">Report a bug</h2>
        <button
            type="button"
            class="report-bug-drawer__close"
            aria-label="Close"
            (click)="onClose()">
            <i class="ph-bold ph-x" aria-hidden="true"></i>
        </button>
    </div>

    <div class="report-bug-drawer__body">
        <!-- Trust panel: reassure the reporter we already grabbed the hard part -->
        <div class="report-bug-trust">
            <div class="report-bug-trust__headline">
                <i class="ph-bold ph-check-circle" aria-hidden="true"></i>
                <span>We captured the tech details for you</span>
            </div>
            <div class="report-bug-chips">
                <span class="report-bug-chip" title="{{ capturedPage }}">
                    <i class="ph ph-file" aria-hidden="true"></i>
                    <span class="report-bug-chip__label">{{ capturedPage }}</span>
                </span>
                <span class="report-bug-chip" title="{{ capturedAccount }}">
                    <i class="ph ph-user" aria-hidden="true"></i>
                    <span class="report-bug-chip__label">{{ capturedAccount }}</span>
                </span>
                <span class="report-bug-chip">
                    <i class="ph ph-cursor-click" aria-hidden="true"></i>
                    <span class="report-bug-chip__label">{{ capturedRecentClicks }} clicks</span>
                </span>
                <span class="report-bug-chip">
                    <i class="ph ph-warning" aria-hidden="true"></i>
                    <span class="report-bug-chip__label">{{ capturedErrors }} errors</span>
                </span>
                <span
                    class="report-bug-chip report-bug-chip--trace"
                    [class.report-bug-chip--trace-captured]="capturedHasTrace">
                    <i class="ph ph-fingerprint" aria-hidden="true"></i>
                    <span class="report-bug-chip__label">{{ capturedHasTrace ? 'trace captured' : 'no errors yet' }}</span>
                </span>
            </div>
            <app-button type="link" class="report-bug-link" [buttonClasses]="['mr-0']" (onClick)="toggleCaptured()">
                {{ showCaptured ? 'hide' : 'view' }} captured context
            </app-button>
            <div class="report-bug-captured" *ngIf="showCaptured">
                <dl>
                    <dt>Page</dt>
                    <dd>{{ capturedPage }}</dd>
                    <dt>Account</dt>
                    <dd>{{ capturedAccount }}</dd>
                    <dt>Browser</dt>
                    <dd>{{ capturedBrowser }}</dd>
                    <dt>Recent clicks</dt>
                    <dd>{{ capturedRecentClicks }}</dd>
                    <dt>Errors captured</dt>
                    <dd>{{ capturedErrors }}</dd>
                    <dt>Trace id</dt>
                    <dd>{{ capturedTraceId }}</dd>
                </dl>
                <p class="report-bug-captured__steps-title" *ngIf="capturedSteps.length">Recent steps</p>
                <ol class="report-bug-captured__steps">
                    <li *ngFor="let step of capturedSteps; trackBy: trackByIndex">{{ step }}</li>
                </ol>
            </div>
        </div>

        <form [formGroup]="form" class="report-bug-form">
            <div class="form-group">
                <label>What went wrong? (one line)</label>
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="report-bug-field">
                    <input matInput formControlName="summary" placeholder="A quick summary" />
                </mat-form-field>
                <lf-control-messages [control]="form.controls.summary"></lf-control-messages>
            </div>

            <div class="form-group">
                <label>What did you expect to happen?</label>
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="report-bug-field">
                    <textarea matInput formControlName="expected" rows="2"></textarea>
                </mat-form-field>
                <lf-control-messages [control]="form.controls.expected"></lf-control-messages>
            </div>

            <div class="form-group">
                <label>What actually happened?</label>
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="report-bug-field">
                    <textarea matInput formControlName="actual" rows="2"></textarea>
                </mat-form-field>
                <lf-control-messages [control]="form.controls.actual"></lf-control-messages>
            </div>

            <div class="report-bug-repro" formArrayName="reproSteps">
                <label class="report-bug-repro__label">How you got here</label>
                <p class="report-bug-repro__hint">
                    We pre-filled these from your recent steps — edit, add or remove as needed.
                </p>
                <div class="report-bug-repro__row" *ngFor="let step of reproSteps.controls; let i = index; trackBy: trackByControl">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="report-bug-repro__field">
                        <input matInput [formControlName]="i" placeholder="Step {{ i + 1 }}" />
                    </mat-form-field>
                    <button
                        type="button"
                        class="report-bug-repro__remove"
                        aria-label="Remove step"
                        (click)="removeReproStep(i)">
                        <i class="ph-bold ph-trash" aria-hidden="true"></i>
                    </button>
                </div>
                <app-button type="link" class="report-bug-link" [buttonClasses]="['mr-0']" (onClick)="addReproStep()">
                    + Add a step
                </app-button>
            </div>

            <div class="report-bug-toggle-group">
                <label class="report-bug-toggle-group__label">How bad?</label>
                <mat-button-toggle-group class="report-bug-segmented" formControlName="severity" aria-label="Severity">
                    <mat-button-toggle *ngFor="let s of severities; trackBy: trackByIndex" [value]="s.value">
                        {{ s.label }}
                    </mat-button-toggle>
                </mat-button-toggle-group>
            </div>

            <div class="report-bug-toggle-group">
                <label class="report-bug-toggle-group__label">Who's affected?</label>
                <mat-button-toggle-group class="report-bug-segmented" formControlName="scope" aria-label="Scope">
                    <mat-button-toggle *ngFor="let s of scopes; trackBy: trackByIndex" [value]="s.value">
                        {{ s.label }}
                    </mat-button-toggle>
                </mat-button-toggle-group>
            </div>

            <div class="report-bug-preview">
                <app-button type="link" class="report-bug-link" [buttonClasses]="['mr-0']" (onClick)="togglePreview()">
                    {{ showPreview ? 'Hide' : 'Preview' }} what we send
                </app-button>
                <pre class="report-bug-preview__json" *ngIf="showPreview">{{ previewJson }}</pre>
            </div>
        </form>
    </div>

    <div class="report-bug-drawer__footer">
        <app-button
            class="report-bug-send"
            type="primary"
            [buttonClasses]="['mr-0']"
            [isWorking]="submitting"
            [isDisabled]="submitting"
            (onClick)="onSend()">
            Send bug report
        </app-button>
    </div>
</div>
